/**
 * Voice Memo Card Component
 * Card view for displaying voice memos
 */

import { Play, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { VoiceMemoPlayer } from './voice-memo-player';
import { StatusBadge } from './voice-memo-status-badge';
import type { VoiceMemo } from './types';

interface VoiceMemoCardProps {
  memo: VoiceMemo;
  onProcess: (id: string) => void;
  onViewTranscript?: (memo: VoiceMemo) => void;
  onReviewAndFile?: (memo: VoiceMemo) => void;
  isTranscribing?: boolean;
  viewMode?: 'card' | 'grid';
  isSelected?: boolean;
}

export function VoiceMemoCard({
  memo,
  onProcess,
  onViewTranscript,
  onReviewAndFile,
  isTranscribing,
  viewMode = 'card',
  isSelected = false
}: VoiceMemoCardProps) {
  const isCurrentlyTranscribing = memo.status === 'transcribing' || isTranscribing;
  const isGrid = viewMode === 'grid';

  return (
    <Card className={`p-4 h-full flex flex-col min-w-0 overflow-hidden transition-all duration-500 ease-in-out ${
      isSelected
        ? 'border-l-2 border-l-primary/50 border border-primary/20 bg-primary/10 hover:bg-primary/15'
        : 'hover:bg-muted/50'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2 gap-2 min-w-0 flex-shrink-0">
        <div className="flex-1 min-w-0 overflow-hidden">
          {memo.title ? (
            <h4 className={`font-semibold mb-1 ${isGrid ? 'line-clamp-1' : 'line-clamp-2'} break-words`} title={memo.title}>
              {memo.title}
            </h4>
          ) : (
            <h4 className={`font-semibold mb-1 truncate text-muted-foreground`} title={memo.filename}>
              {memo.filename}
            </h4>
          )}
          {!isGrid && memo.title && (
            <p className="text-xs text-muted-foreground truncate mb-1" title={memo.filename}>
              {memo.filename}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <StatusBadge status={memo.status} />
        </div>
      </div>

      {/* Metadata - Simplified for grid, full for card - responsive hiding */}
      <div className={`flex items-center gap-1 text-xs text-muted-foreground mb-2 min-w-0 flex-shrink-0 ${isGrid ? 'flex-wrap' : 'flex-nowrap overflow-hidden'}`}>
        <span className="flex-shrink-0 whitespace-nowrap">{memo.date}</span>
        {memo.timestamp && (
          <>
            <span className="flex-shrink-0 hidden sm:inline">•</span>
            <span className="flex-shrink-0 whitespace-nowrap hidden sm:inline">{memo.timestamp.substring(0, 2)}:{memo.timestamp.substring(2, 4)}</span>
          </>
        )}
        <span className="flex-shrink-0">•</span>
        <span className="flex-shrink-0 whitespace-nowrap">{memo.duration}</span>
        <span className="flex-shrink-0 hidden md:inline">•</span>
        <span className="flex-shrink-0 whitespace-nowrap hidden md:inline">{memo.fileSize}</span>
        {!isGrid && memo.wordCount && (
          <>
            <span className="flex-shrink-0 hidden lg:inline">•</span>
            <span className="flex-shrink-0 whitespace-nowrap hidden lg:inline">{memo.wordCount} words</span>
          </>
        )}
      </div>

      {/* Summary (if analyzed) - Only in card view */}
      {!isGrid && memo.status === 'analyzed' && memo.summary && (
        <p className="text-sm text-foreground mb-2 line-clamp-2 flex-shrink-0">
          {memo.summary}
        </p>
      )}

      {/* Theme badge (if analyzed) - Only in card view */}
      {!isGrid && memo.theme && memo.status === 'analyzed' && (
        <div className="flex flex-wrap gap-1.5 mb-2 flex-shrink-0">
          <Badge variant="secondary" className="text-xs">
            {memo.theme}
          </Badge>
        </div>
      )}

      {/* Audio Player */}
      <div className={`mb-2 min-w-0 flex-shrink-0 ${isGrid ? 'overflow-hidden' : ''}`}>
        <VoiceMemoPlayer memo={memo} />
      </div>

      {/* Action Button */}
      <div className="mt-auto pt-2 border-t flex-shrink-0">
        {memo.status === 'unprocessed' && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="default"
              onClick={() => onProcess(memo.id)}
              disabled={isCurrentlyTranscribing}
            >
              {isCurrentlyTranscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transcribing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Transcribe
                </>
              )}
            </Button>
          </div>
        )}

        {memo.status === 'transcribing' && (
          <Button
            size="sm"
            variant="default"
            className="w-full"
            disabled
          >
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Transcribing...
          </Button>
        )}

        {memo.status === 'transcribed' && (
          <button
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => onViewTranscript?.(memo)}
            disabled={!memo.transcript?.path}
          >
            <span>View transcript</span>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )}

        {memo.status === 'analyzed' && (
          <button
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => onReviewAndFile?.(memo)}
          >
            <span>View analysis</span>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>
    </Card>
  );
}
